/**
 * Enterprise-Level Logout Utility
 *
 * Provides comprehensive, atomic, cross-browser compatible logout mechanism that:
 * - Clears all browser storage (localStorage, sessionStorage, cookies)
 * - Clears IndexedDB databases
 * - Clears Cache API storage
 * - Unregisters Service Workers and clears their caches
 * - Clears browser history state
 * - Resets Redux store to initial state with persist purge
 * - Invalidates React Query cache (if present)
 * - Resets Zustand stores (if present)
 * - Clears axios interceptors and tokens
 * - Performs hard reload to clear HTTP cache and in-memory state
 * - Prevents race conditions
 * - Supports multi-user scenarios
 * - Preserves user preferences (theme) across sessions
 *
 * Browser Compatibility:
 * - Chrome 40+
 * - Firefox 39+
 * - Safari 10+ (11.1+ for full Service Worker support)
 * - Edge 79+ (Chromium-based)
 * - Opera 27+
 */

import { store } from '../../store/ReduxStore';
import { persistor } from '../../store/ReduxStore';
import { resetUserParams } from '../../store/ReduxUser';
import { clearAccessRights } from '../../store/ReduxAccessRight';
import { clearAccessModules } from '../../store/ReduxAccessModules';
import { clearmoduleParamJson } from '../../store/ReduxModuleParam';
import { clearGrnData } from '../../store/ReduxGrn';
import { GRN_STORAGE_KEYS } from '../sys/GrnConstants';
import {
  clearIndexedDB,
  clearCacheAPI,
  clearServiceWorkers,
  clearHistoryState,
  performHardReload
} from '../browser/browserCacheUtils';

/**
 * List of all localStorage keys that contain user-specific or session-specific data
 * These will be explicitly removed during logout
 */
const USER_SCOPED_STORAGE_KEYS = [
  // Redux Persist keys
  'persist:user',
  'persist:accessRights',
  'persist:accessModules',
  'persist:moduleParamJson',
  'persist:grn',
  'persist:root',
  
  // GRN-specific keys
  GRN_STORAGE_KEYS.GRN_VIEW_DATA,
  GRN_STORAGE_KEYS.GRN_PAGE_DATA,
  GRN_STORAGE_KEYS.RETURNED_PRODUCTS,
  GRN_STORAGE_KEYS.BILLING_SUMMARY,
  GRN_STORAGE_KEYS.NEXT_PROCESS_RESPONSE,
  
  // Common auth/session keys
  'token',
  'accessToken',
  'refreshToken',
  'jwt',
  'authToken',
  'userId',
  'userRefCode',
  'userData',
  'sessionId',
  'rememberMe',
  'autoLogin',
  
  // Application-specific keys (add more as needed)
  'filters',
  'cart',
  'orders',
  'transactions',
  'offlineDrafts',
  'dataGridModels',
  'dataGridState',
  'gridFilters',
  'gridSort',
  'gridPagination',
];

/**
 * List of cookie names that should be deleted on logout
 * Supports wildcard patterns for domain-specific cookies
 */
const AUTH_COOKIE_PATTERNS = [
  'token',
  'accessToken',
  'refreshToken',
  'jwt',
  'authToken',
  'sessionId',
  'userId',
  'userRefCode',
  'rememberMe',
  'autoLogin',
  'auth',
  'session',
];

/**
 * Cross-browser cookie deletion utility
 * Works on Chrome, Edge, Firefox, Safari
 */
const deleteCookie = (name, path = '/', domain = null) => {
  // Standard deletion
  document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=${path};`;
  
  // Domain-specific deletion (if domain provided)
  if (domain) {
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=${path}; domain=${domain};`;
    // Also try without leading dot
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=${path}; domain=.${domain};`;
  }
  
  // Try with secure flag
  document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=${path}; secure;`;
  
  // Try with SameSite=None
  document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=${path}; SameSite=None; secure;`;
  
  // Try with SameSite=Lax
  document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=${path}; SameSite=Lax;`;
  
  // Try with SameSite=Strict
  document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=${path}; SameSite=Strict;`;
};

/**
 * Delete all cookies matching the auth patterns
 */
const clearAuthCookies = () => {
  try {
    // Get current domain and parent domains
    const hostname = window.location.hostname;
    const domainParts = hostname.split('.');
    const domains = [hostname];
    
    // Add parent domains (e.g., for subdomain.example.com, also try example.com)
    if (domainParts.length > 2) {
      domains.push(domainParts.slice(-2).join('.'));
    }
    
    // Delete cookies for each pattern and domain combination
    AUTH_COOKIE_PATTERNS.forEach(pattern => {
      domains.forEach(domain => {
        deleteCookie(pattern, '/', domain);
        deleteCookie(pattern, '/', null);
      });
    });
    
    // Also delete all cookies visible to JavaScript
    // Note: This only works for cookies accessible via document.cookie
    const cookies = document.cookie.split(';');
    cookies.forEach(cookie => {
      const eqPos = cookie.indexOf('=');
      const name = eqPos > -1 ? cookie.substr(0, eqPos).trim() : cookie.trim();
      
      // Delete if it matches any pattern
      if (AUTH_COOKIE_PATTERNS.some(pattern => 
        name.toLowerCase().includes(pattern.toLowerCase())
      )) {
        domains.forEach(domain => {
          deleteCookie(name, '/', domain);
          deleteCookie(name, '/', null);
        });
      }
    });
  } catch (error) {
    console.warn('Error clearing cookies:', error);
  }
};

/**
 * Clear all localStorage keys (selective + fallback to clear)
 */
const clearLocalStorage = () => {
  try {
    // First, explicitly remove known user-scoped keys
    USER_SCOPED_STORAGE_KEYS.forEach(key => {
      try {
        localStorage.removeItem(key);
      } catch (error) {
        console.warn(`Failed to remove localStorage key: ${key}`, error);
      }
    });
    
    // Then, remove any keys that match common patterns
    const keysToRemove = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key) {
        // Check if key contains auth-related terms
        const lowerKey = key.toLowerCase();
        if (
          lowerKey.includes('token') ||
          lowerKey.includes('auth') ||
          lowerKey.includes('user') ||
          lowerKey.includes('session') ||
          lowerKey.includes('jwt') ||
          lowerKey.includes('grn') ||
          lowerKey.includes('cart') ||
          lowerKey.includes('order') ||
          lowerKey.includes('filter') ||
          lowerKey.includes('grid') ||
          lowerKey.startsWith('persist:')
        ) {
          keysToRemove.push(key);
        }
      }
    }
    
    keysToRemove.forEach(key => {
      try {
        localStorage.removeItem(key);
      } catch (error) {
        console.warn(`Failed to remove localStorage key: ${key}`, error);
      }
    });
    
    // Finally, clear everything as a safety measure
    // Note: We preserve themeMode as it's a user preference, not session data
    const themeMode = localStorage.getItem('themeMode');
    localStorage.clear();
    if (themeMode) {
      localStorage.setItem('themeMode', themeMode);
    }
  } catch (error) {
    console.error('Error clearing localStorage:', error);
    // Fallback: try to clear everything
    try {
      const themeMode = localStorage.getItem('themeMode');
      localStorage.clear();
      if (themeMode) {
        localStorage.setItem('themeMode', themeMode);
      }
    } catch (fallbackError) {
      console.error('Fallback localStorage clear also failed:', fallbackError);
    }
  }
};

/**
 * Clear all sessionStorage
 */
const clearSessionStorage = () => {
  try {
    sessionStorage.clear();
  } catch (error) {
    console.error('Error clearing sessionStorage:', error);
  }
};

/**
 * Reset Redux store to initial state
 */
const resetReduxStore = async () => {
  try {
    // Dispatch reset actions for all slices
    store.dispatch(resetUserParams());
    store.dispatch(clearAccessRights());
    store.dispatch(clearAccessModules());
    store.dispatch(clearmoduleParamJson());
    store.dispatch(clearGrnData());
    
    // Purge redux-persist storage
    await persistor.purge();
    
    // Wait a tick to ensure all dispatches are processed
    await new Promise(resolve => setTimeout(resolve, 0));
  } catch (error) {
    console.error('Error resetting Redux store:', error);
  }
};

/**
 * Invalidate React Query cache (if React Query is present)
 */
const invalidateReactQueryCache = () => {
  try {
    // Check if React Query is available
    // This is a no-op if React Query is not installed
    if (typeof window !== 'undefined' && window.__REACT_QUERY_CLIENT__) {
      const queryClient = window.__REACT_QUERY_CLIENT__;
      queryClient.clear();
      queryClient.getQueryCache().clear();
      queryClient.getMutationCache().clear();
    }
  } catch (error) {
    // Silently fail if React Query is not available
    console.debug('React Query not available or error invalidating cache:', error);
  }
};

/**
 * Reset Zustand stores (if Zustand is present)
 * This is a placeholder for future Zustand integration
 */
const resetZustandStores = () => {
  try {
    // If Zustand stores are registered globally, reset them here
    if (typeof window !== 'undefined' && window.__ZUSTAND_STORES__) {
      window.__ZUSTAND_STORES__.forEach(resetFn => {
        try {
          resetFn();
        } catch (error) {
          console.warn('Error resetting Zustand store:', error);
        }
      });
    }
  } catch (error) {
    // Silently fail if Zustand is not available
    console.debug('Zustand not available or error resetting stores:', error);
  }
};

/**
 * Clear axios interceptors and reset token
 * This is handled by the axios interceptor setup, but we ensure cleanup here
 */
const clearAxiosInterceptors = () => {
  try {
    // Reset the token variable in externalApis.jsx
    // This is a workaround since we can't directly access the module's private variable
    // The interceptor setup should handle this, but we ensure it's cleared
    if (typeof window !== 'undefined') {
      window.__AXIOS_TOKEN__ = null;
    }
  } catch (error) {
    console.warn('Error clearing axios interceptors:', error);
  }
};

/**
 * Main logout function - comprehensive and atomic
 *
 * Clears ALL browser storage including:
 * - localStorage, sessionStorage, cookies
 * - Redux store (with redux-persist purge)
 * - IndexedDB databases
 * - Cache API storage
 * - Service Worker caches
 * - React Query cache
 * - Zustand stores
 * - Axios interceptor tokens
 * - Browser history state
 *
 * Then performs hard reload to clear HTTP cache and in-memory state.
 *
 * @param {Object} options - Logout options
 * @param {boolean} options.preserveTheme - Preserve theme preference (default: true)
 * @param {Function} options.onProgress - Progress callback (step, progress, completed)
 * @param {Function} options.onComplete - DEPRECATED: Not called due to hard reload
 * @returns {Promise<void>}
 */
export const logout = async (options = {}) => {
  const {
    preserveTheme = true,
    onComplete = null,
    onProgress = null,
  } = options;
  
  try {
    // Preserve theme if requested
    const themeMode = preserveTheme ? localStorage.getItem('themeMode') : null;
    const completedSteps = [];

    // Execute all cleanup operations
    // Order matters: Redux first, then storage, then cookies

    // 1. Reset Redux store (this also triggers redux-persist purge)
    if (onProgress) onProgress("🔄 Resetting application state...", 10, completedSteps);
    await resetReduxStore();
    completedSteps.push("State reset");

    // 2. Clear browser storage
    if (onProgress) onProgress("🗑️ Clearing browser storage...", 20, completedSteps);
    clearLocalStorage();
    clearSessionStorage();
    completedSteps.push("Storage cleared");

    // 3. Clear cookies
    if (onProgress) onProgress("🍪 Removing cookies...", 30, completedSteps);
    clearAuthCookies();
    completedSteps.push("Cookies removed");

    // 4. Invalidate React Query cache (if present)
    if (onProgress) onProgress("📦 Clearing cached data...", 40, completedSteps);
    invalidateReactQueryCache();

    // 5. Reset Zustand stores (if present)
    resetZustandStores();

    // 6. Clear axios interceptors
    clearAxiosInterceptors();
    completedSteps.push("Cache cleared");

    // 7. Clear IndexedDB
    if (onProgress) onProgress("💾 Clearing database...", 50, completedSteps);
    try {
      const indexedDBResult = await clearIndexedDB();
      if (!indexedDBResult.success && indexedDBResult.errors.length > 0) {
        console.warn('IndexedDB clearing had errors:', indexedDBResult.errors);
      }
      completedSteps.push("Database cleared");
    } catch (error) {
      console.warn('IndexedDB clearing failed:', error);
    }

    // 8. Clear Cache API
    if (onProgress) onProgress("🗂️ Clearing cache files...", 60, completedSteps);
    try {
      const cacheResult = await clearCacheAPI();
      if (!cacheResult.success && cacheResult.errors.length > 0) {
        console.warn('Cache API clearing had errors:', cacheResult.errors);
      }
      completedSteps.push("Cache files cleared");
    } catch (error) {
      console.warn('Cache API clearing failed:', error);
    }

    // 9. Clear Service Workers
    if (onProgress) onProgress("⚙️ Clearing service workers...", 70, completedSteps);
    try {
      const swResult = await clearServiceWorkers();
      if (!swResult.success && swResult.errors.length > 0) {
        console.warn('Service Worker clearing had errors:', swResult.errors);
      }
      completedSteps.push("Service workers cleared");
    } catch (error) {
      console.warn('Service Worker clearing failed:', error);
    }

    // 10. Clear history state
    if (onProgress) onProgress("📜 Clearing history...", 80, completedSteps);
    clearHistoryState();
    completedSteps.push("History cleared");

    // 11. Clear any remaining in-memory references
    if (onProgress) onProgress("🧹 Final cleanup...", 90, completedSteps);
    if (typeof window !== 'undefined') {
      // Clear any global state references
      delete window.__AXIOS_TOKEN__;
      delete window.__USER_DATA__;
      delete window.__SESSION_DATA__;
    }

    // 12. Force garbage collection hint (browser-dependent)
    if (window.gc) {
      try {
        window.gc();
      } catch (e) {
        // Ignore if garbage collection is not available
      }
    }

    // 13. Restore theme if preserved
    if (preserveTheme && themeMode) {
      localStorage.setItem('themeMode', themeMode);
    }
    completedSteps.push("Cleanup complete");

    console.log('Logout completed successfully - preparing hard reload');

    // 14. Perform hard reload (must be last)
    if (onProgress) onProgress("🚀 Reloading page...", 100, completedSteps);

    // Note: onComplete callback will NOT be called as page reloads
    setTimeout(() => {
      performHardReload();
    }, 100); // Small delay to ensure storage operations complete
  } catch (error) {
    console.error('Error during logout:', error);
    // Even if there's an error, try to clear storage as fallback
    try {
      clearLocalStorage();
      clearSessionStorage();
      clearAuthCookies();
    } catch (fallbackError) {
      console.error('Fallback cleanup also failed:', fallbackError);
    }
    throw error;
  }
};

/**
 * Synchronous logout for immediate execution (without async operations)
 * Use this when you need immediate cleanup before navigation (e.g., 401 errors)
 *
 * Note: This performs synchronous clearing only (localStorage, sessionStorage, cookies)
 * and schedules async operations (IndexedDB, Cache API, Service Workers) in background.
 */
export const logoutSync = (options = {}) => {
  const {
    preserveTheme = true,
  } = options;

  try {
    const themeMode = preserveTheme ? localStorage.getItem('themeMode') : null;

    // Clear storage synchronously
    clearLocalStorage();
    clearSessionStorage();
    clearAuthCookies();

    // Reset Redux synchronously (may not purge persist immediately)
    store.dispatch(resetUserParams());
    store.dispatch(clearAccessRights());
    store.dispatch(clearAccessModules());
    store.dispatch(clearmoduleParamJson());
    store.dispatch(clearGrnData());

    // Invalidate caches
    invalidateReactQueryCache();
    resetZustandStores();
    clearAxiosInterceptors();

    // Clear history state (synchronous)
    clearHistoryState();

    // Clear any remaining in-memory references
    if (typeof window !== 'undefined') {
      delete window.__AXIOS_TOKEN__;
      delete window.__USER_DATA__;
      delete window.__SESSION_DATA__;
    }

    // Restore theme
    if (preserveTheme && themeMode) {
      localStorage.setItem('themeMode', themeMode);
    }

    // Purge persist asynchronously in background
    persistor.purge().catch(err => {
      console.warn('Background persist purge failed:', err);
    });

    // Clear IndexedDB, Cache API, and Service Workers asynchronously in background
    // These don't block the sync logout but ensure complete cleanup
    Promise.all([
      clearIndexedDB().catch(err => console.warn('Background IndexedDB clear failed:', err)),
      clearCacheAPI().catch(err => console.warn('Background Cache API clear failed:', err)),
      clearServiceWorkers().catch(err => console.warn('Background Service Worker clear failed:', err))
    ]).then(() => {
      console.log('Background async clearing completed');
    });

    console.log('Sync logout completed - preparing hard reload');

    // Perform hard reload after a small delay
    setTimeout(() => {
      performHardReload();
    }, 100);
  } catch (error) {
    console.error('Error during sync logout:', error);
    // Force hard reload even on error
    setTimeout(() => {
      performHardReload();
    }, 100);
  }
};

export default logout;

